import type {
	ActionFunctionArgs,
	LoaderFunctionArgs,
	MetaFunction
} from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import { useSearchParams, useSubmit } from "@remix-run/react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { LoginForm } from "~/components/LoginForm";
import Logo from "~/components/Logo";
import { SetNewPasswordForm } from "~/components/SetNewPasswordForm";
import {
	authSignIn,
	buildAuthFormData,
	confirmNewPassword,
	getClientAuthSession,
	needsNewPassword
} from "~/lib/auth-client";
import { commitAuthCookieHeaders, requireGuest } from "~/lib/auth.server";
import { safeRedirect } from "~/lib/auth-utils";

export const meta: MetaFunction = () => {
	return [
		{ title: "Login | Ampere CMS" },
		{ name: "description", content: "Log in to start managing your business" }
	];
};

export async function loader({ request }: LoaderFunctionArgs) {
	const headers = await requireGuest(request);
	return json({}, headers ? { headers } : undefined);
}

export const action = async ({ request }: ActionFunctionArgs) => {
	const form = await request.formData();
	const strAuthCookie = form.get("auth_cookie");
	const strToken = form.get("token");

	if (typeof strAuthCookie === "string" && typeof strToken === "string") {
		let authCookie: unknown;
		try {
			authCookie = JSON.parse(strAuthCookie);
		} catch {
			return json(
				{ statusCode: 400, message: "Invalid session" },
				{ status: 400 }
			);
		}

		const url = new URL(request.url);
		const headers = await commitAuthCookieHeaders(
			request,
			authCookie,
			strToken
		);
		return redirect(safeRedirect(url.searchParams.get("redirect")), {
			headers
		});
	}

	return json({ statusCode: 400, message: "Missing session" }, { status: 400 });
};

export default function Index() {
	const [isLoading, setIsLoading] = useState(false);
	const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
	const submit = useSubmit();
	const [params] = useSearchParams();
	const defaultEmail = params.get("email") || "";
	const syncAttempted = useRef(false);

	useEffect(() => {
		if (syncAttempted.current || defaultEmail) return;
		syncAttempted.current = true;

		async function syncExistingSession() {
			const existing = await getClientAuthSession();
			if (existing.status !== "ok") return;
			setIsLoading(true);
			submit(buildAuthFormData(existing.session, existing.token), {
				method: "POST"
			});
		}

		void syncExistingSession();
	}, [defaultEmail, submit]);

	async function completeLogin() {
		const existing = await getClientAuthSession();
		if (existing.status !== "ok") {
			toast("Something went wrong. Try again.");
			setIsLoading(false);
			return;
		}
		submit(buildAuthFormData(existing.session, existing.token), {
			method: "POST"
		});
	}

	async function handleLogin(values: FormData) {
		setIsLoading(true);
		const email = values.get("email");
		const password = values.get("password");

		if (typeof email !== "string" || typeof password !== "string") {
			toast("Something went wrong. Try again.");
			setIsLoading(false);
			return;
		}

		try {
			const result = await authSignIn(email, password);
			if (needsNewPassword(result)) {
				setNeedsPasswordReset(true);
				setIsLoading(false);
				return;
			}
			await completeLogin();
		} catch {
			setIsLoading(false);
		}
	}

	async function handleNewPassword(password: string) {
		setIsLoading(true);
		try {
			const result = await confirmNewPassword(password);
			if (needsNewPassword(result)) {
				toast("Please choose a new password to continue.");
				setIsLoading(false);
				return;
			}
			await completeLogin();
		} catch (error) {
			toast(
				error instanceof Error
					? error.message
					: "Unable to set your new password. Try again."
			);
			setIsLoading(false);
		}
	}

	return (
		<div className="w-full h-screen flex flex-col items-center justify-center">
			<div className="h-16 w-full justify-center items-center gap-x-2 mb-12 flex select-none">
				<div className="max-size-16 size-16">
					<Logo />
				</div>
				<h3 className="font-bold text-4xl text-slate-950">CMS</h3>
			</div>
			<div>
				{needsPasswordReset ? (
					<SetNewPasswordForm
						submit={handleNewPassword}
						isLoading={isLoading}
					/>
				) : (
					<LoginForm
						submit={(values) => handleLogin(values)}
						isLoading={isLoading}
						defaultEmail={defaultEmail}
					/>
				)}
			</div>
		</div>
	);
}
